import type { FunctionalComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { SafeImage } from '@/components/safe-image.tsx';
import { NavBack } from '@/components/nav-back.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import {
  CheckIcon,
  ChevronIcon,
  KakaoBubbleIcon,
  KakaoIcon,
  PencilIcon,
  PlayTriangleIcon,
  YoutubeIcon,
} from '@/components/event/event-icons.tsx';
import { MSG } from '@/lib/constants.ts';
import {
  agreeEvent,
  fetchEvent,
  fetchEventNotes,
  fetchEventProgress,
  fetchShareStatus,
  submitEventReview,
  submitEventShare,
  uploadEventShot,
} from '@/lib/event.api.ts';
import { SHARE_MESSAGE, isShared, preloadKakao, share } from '@/lib/share.ts';
import { sendMobileCode, verifyMobileCode } from '@/lib/user.api.ts';
import { absoluteUrl, digits, formatMinSec, returnUrl, shareUrl } from '@/lib/utils.ts';
import {
  MOBILE_CODE_LENGTH,
  MOBILE_CODE_TTL_SEC,
  MOBILE_RESEND_COOLDOWN_SEC,
} from '@/data/policy.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type {
  EventDetail,
  EventMission,
  EventMissionKind,
  EventMissionProgress,
  EventProgress,
  EventShareStatus,
} from '@/types/event.ts';
import type { PageProps } from '@/types/props.ts';

/** `\n` 을 줄바꿈으로 */
const Lines: FunctionalComponent<{ text: string }> = ({ text }) => (
  <>
    {String(text ?? '')
      .split('\n')
      .map((line, i) => (
        <>
          {i > 0 && <br />}
          {line}
        </>
      ))}
  </>
);

/** `*강조*` 를 시안의 `<em>` 으로 */
const Heading: FunctionalComponent<{ text: string }> = ({ text }) => (
  <>
    {String(text ?? '')
      .split('\n')
      .map((line, i) => (
        <>
          {i > 0 && <br />}
          {line.split('*').map((part, j) => (j % 2 ? <em key={j}>{part}</em> : part))}
        </>
      ))}
  </>
);

/**
 * 경품을 몇 칸으로 늘어놓을지. 개수는 관리자가 DB 에 넣은 만큼 달라진다.
 *
 * 시안은 두 칸으로 못 박혀 있다. 그래서 경품이 하나뿐인 이벤트는 그 하나가
 * 왼쪽 칸에만 들어가고 오른쪽이 빈 채로 남아, 가운데가 아니라 왼쪽으로
 * 치우쳐 보였다.
 *
 * 마지막 줄에 하나만 덩그러니 남지 않도록 개수에 맞춰 나눈다.
 *
 *   1 → 1칸      3 → 3칸        5 → 3 + 2
 *   2 → 2칸      4 → 2 + 2      6 이상 → 3칸씩
 *
 * 칸 수는 `data-columns` 로 내려보내고 _overrides.scss 가 받는다.
 */
function prizeColumns(count: number): number {
  if (count <= 3) return Math.max(count, 1);
  if (count === 4) return 2;
  return 3;
}

const MISSION_ICON: Record<EventMissionKind, FunctionalComponent> = {
  share: KakaoIcon,
  comment: YoutubeIcon,
  review: PencilIcon,
};

const EMPTY_PROGRESS: EventProgress = { entries: 0, agreed: false, mobile: null, missions: [] };

/**
 * 카카오 공유 결과를 기다리는 최대 시간.
 *
 * 채팅방을 고르고 보내는 데 14~17초 걸린 기록이 있다. 넉넉히 잡고, 그 안에 결과가
 * 없으면 공유창을 그냥 닫은 것으로 보고 조용히 끝낸다.
 */
const SHARE_WAIT_MS = 3 * 60 * 1000;

/** 확인 팝업을 거쳐야 보낼 수 있는 참여 요청 */
type Pending =
  /** `platform` 은 DB 코드(K/F/...), `platformId` 는 공유 방식 이름(kakao/...) */
  | { kind: 'share'; missionSeq: number; platform: string; platformId: string }
  | { kind: 'upload'; missionSeq: number; file: File }
  | { kind: 'review'; missionSeq: number; content: string };

/**
 * 이벤트 상세 (시안 `_event-view.html`).
 *
 * 내용은 `MC_EVENT` 에서 온다. **MISSION 번호와 유의사항의 숫자·날짜는 서버가
 * 채운 값을 그대로 쓴다** — 화면이 계산하면 관리자가 순서나 한도를 바꿨을 때
 * 문구와 어긋난다. 참여 횟수도 마찬가지로 서버가 준 값만 그린다.
 *
 * 참여는 회원만 할 수 있고, **경품 발송 번호가 있어야** 응모권이 나간다. 번호가
 * 아직 없으면 어떤 버튼을 눌러도 먼저 응모 정보 입력 팝업이 뜬다.
 */
const EventView: FunctionalComponent<PageProps<{ id?: string }>> = ({ id }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const userSeq = Number(user?.userSeq ?? 0);

  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [progress, setProgress] = useState<EventProgress>(EMPTY_PROGRESS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [notesPage, setNotesPage] = useState(1);
  const [notes, setNotes] = useState<EventDetail['notes']>({ items: [], total: 0, total_pages: 1 });
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  /**
   * 카카오톡 공유에 쓸 1회용 토큰.
   *
   * **미리 받아 둔다.** 버튼을 누른 뒤에 서버를 다녀오면 그 사이 사용자 조작이
   * 끊긴 것으로 보여 공유창이 차단될 수 있다.
   */
  const [shareToken, setShareToken] = useState('');
  /**
   * 지금 돌고 있는 공유 대기의 번호.
   *
   * 새 공유를 시작하면 번호가 바뀌어 이전 대기는 스스로 멈춘다. 이게 없으면 대기가
   * 겹쳐 경고가 여러 번 떴다.
   */
  const shareWaitRef = useRef(0);

  // 응모 정보 입력 팝업
  const [pending, setPending] = useState<Pending | null>(null);
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  /** 인증이 끝난 번호. 번호를 고치면 자동으로 인증이 풀린다 */
  const [verifiedMobile, setVerifiedMobile] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const [consents, setConsents] = useState<boolean[]>([]);
  const codeRef = useRef<HTMLInputElement>(null);

  const slug = id ?? '';
  const mobile = `${phone1}${phone2}${phone3}`;
  const phoneFilled = /^01[0-9]\d{7,8}$/.test(mobile);
  const verified = phoneFilled && mobile === verifiedMobile;
  const allAgreed = consents.length > 0 && consents.every(Boolean);

  // 상세
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetchEvent(slug, userSeq);
        if (cancelled) return;
        if (!response.result || !response.data) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          route('/event', true);
          return;
        }
        setDetail(response.data);
        setNotes(response.data.notes ?? { items: [] });
        setProgress(response.data.progress ?? EMPTY_PROGRESS);
        setConsents((response.data.consents ?? []).map(() => false));
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, userSeq, dispatch]);

  /**
   * 카카오톡 공유 항목이 있으면 SDK 를 미리 받아 둔다.
   *
   * 버튼을 누른 뒤에 내려받으면 그 사이에 사용자 조작이 끊긴 것으로 보여
   * 공유창이 차단될 수 있다.
   */
  useEffect(() => {
    const hasKakao = (detail?.missions ?? []).some((mission) =>
      mission.platforms.some((platform) => platform.id === 'kakao')
    );
    if (hasKakao) preloadKakao();
  }, [detail]);

  // 감상평 페이지 이동
  useEffect(() => {
    if (!detail) return;
    let cancelled = false;

    void (async () => {
      const response = await fetchEventNotes(slug, notesPage);
      if (cancelled || !response.result || !response.data) return;
      setNotes(response.data);
    })();

    return () => {
      cancelled = true;
    };
  }, [notesPage, slug, detail]);

  // 남은 시간 카운트다운
  useEffect(() => {
    if (resendIn <= 0 && expiresIn <= 0) return;
    const timer = window.setInterval(() => {
      setResendIn((v) => (v > 0 ? v - 1 : 0));
      setExpiresIn((v) => (v > 0 ? v - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendIn, expiresIn]);

  /**
   * 회원이 이미 인증해 둔 번호는 채워 놓고 인증도 끝난 것으로 본다.
   *
   * 같은 번호를 또 인증시키면 문자만 한 번 더 나갈 뿐 얻는 게 없다. 번호를
   * 고치면 `verified` 가 파생값이라 저절로 풀린다.
   */
  useEffect(() => {
    if ((user?.mobileAuth ?? 'N') !== 'Y') return;

    const number = digits(user?.mobile ?? '');
    if (!/^01[0-9]\d{7,8}$/.test(number)) return;

    setPhone1(number.slice(0, 3));
    setPhone2(number.slice(3, number.length - 4));
    setPhone3(number.slice(-4));
    setVerifiedMobile(number);
  }, [user?.mobile, user?.mobileAuth]);

  // 번호를 고치면 발송 상태도 처음으로
  useEffect(() => {
    setCodeSent(false);
    setCode('');
    setResendIn(0);
    setExpiresIn(0);
  }, [mobile]);

  function requireLogin(): boolean {
    if (userSeq > 0) return true;
    route(`/login?r=${encodeURIComponent(returnUrl())}`);
    return false;
  }

  function numericInput(setter: (v: string) => void, maxLength: number) {
    return (e: Event) => {
      const input = e.currentTarget as HTMLInputElement;
      setter(digits(input.value).slice(0, maxLength));
    };
  }

  function progressOf(missionSeq: number): EventMissionProgress | undefined {
    return progress.missions.find((m) => m.seq === missionSeq);
  }

  /**
   * 참여 요청을 보낸다.
   *
   * 발송 번호가 아직 없으면 보내지 않고 팝업을 띄운다 — 당첨돼도 경품을 보낼
   * 곳이 없으면 응모권이 의미가 없다.
   */
  async function submit(request: Pending, enteredMobile?: string) {
    if (!requireLogin() || busy) return;

    // 동의는 이벤트당 한 번이다. 응모권 유무와 무관하게 서버가 기억한다 —
    // 공유가 실패해 응모권이 안 생겨도 팝업을 다시 띄우지 않는다.
    if (!enteredMobile && !progress.agreed) {
      setPending(request);
      return;
    }

    if (request.kind === 'share') {
      await runShare(request, enteredMobile);
      return;
    }

    setBusy(true);
    try {
      const response =
        request.kind === 'review'
          ? await submitEventReview(
              slug,
              userSeq,
              request.missionSeq,
              request.content,
              enteredMobile
            )
          : await uploadEventShot(slug, userSeq, request.missionSeq, request.file, enteredMobile);

      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      setProgress(response.data ?? EMPTY_PROGRESS);
      setPending(null);
      if (request.kind === 'review') setNote('');
      if (request.kind === 'upload') setFile(null);
      if (response.message) await dispatch(askAlert(response.message));
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  /**
   * 응모 정보 팝업의 확인.
   *
   * **동의를 먼저 남긴다.** 그 다음에 하려던 일(공유·업로드·감상평)을 잇는다.
   * 순서가 중요하다 — 공유가 실패해도 동의는 남아야 팝업이 다시 뜨지 않는다.
   */
  async function confirmAgree() {
    if (!pending || busy) return;

    setBusy(true);
    try {
      const response = await agreeEvent(slug, userSeq, mobile);
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }
      setProgress(response.data ?? EMPTY_PROGRESS);
    } catch {
      await dispatch(askAlert(MSG.apiFail));
      return;
    } finally {
      setBusy(false);
    }

    const request = pending;
    setPending(null);
    await submit(request, mobile);
  }

  /** 공유 시작에 쓸 토큰을 서버에서 받아 둔다. 실패는 조용히 넘긴다. */
  async function issueShareToken(missionSeq: number, platform: string, mobile?: string) {
    try {
      const response = await submitEventShare(slug, userSeq, missionSeq, platform, mobile);
      if (!response.result) return;
      setProgress(response.data ?? EMPTY_PROGRESS);
      setShareToken(response.data?.token ?? '');
    } catch {
      // 한도를 다 썼거나 기간이 끝난 경우다. 버튼이 이미 잠겨 있다.
    }
  }

  /**
   * SNS 공유.
   *
   * **카카오톡은 진짜로 보냈을 때만 응모권이 나간다.** 우리가 넘긴 토큰을 카카오가
   * 전송 후 우리 서버로 되돌려주고, 그때 응모권이 생긴다. 그래서 여기서는 공유창을
   * 띄운 뒤 **잠깐 기다렸다가 현황을 다시 읽는다** — 응모권은 우리 손이 아니라
   * 카카오의 연락으로 생긴다.
   *
   * 나머지 플랫폼은 그런 연락이 없다. 공유창을 띄운 것까지만 알 수 있어 그 시점에
   * 바로 응모권을 준다. 한도가 유일한 방어선이다.
   */
  async function runShare(request: Extract<Pending, { kind: 'share' }>, enteredMobile?: string) {
    const confirmable = request.platformId === 'kakao';
    let token = confirmable ? shareToken : '';

    // 미리 받아 둔 토큰이 없으면 지금 받는다. 이 경로는 팝업이 막힐 수 있어 차선이다.
    if (confirmable && !token) {
      setBusy(true);
      try {
        const response = await submitEventShare(
          slug,
          userSeq,
          request.missionSeq,
          request.platform,
          enteredMobile
        );
        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return;
        }
        setProgress(response.data ?? EMPTY_PROGRESS);
        token = response.data?.token ?? '';
      } catch {
        await dispatch(askAlert(MSG.apiFail));
        return;
      } finally {
        setBusy(false);
      }
    }

    const outcome = await share(request.platformId, {
      title: detail?.title ?? '',
      description: detail?.description ?? '',
      url: shareUrl(`/event/${slug}`),
      imageUrl: absoluteUrl(detail?.thumbnail),
      callbackToken: token || undefined,
    });

    if (!isShared(outcome.result)) {
      // 카카오가 알려준 사유가 있으면 그대로 보여준다. 도메인 미등록처럼 설정에서
      // 고쳐야 하는 것들이 여기로 나온다.
      const reason = SHARE_MESSAGE[outcome.result];
      await dispatch(askAlert(outcome.message ? `${reason}\n(${outcome.message})` : reason));
      return;
    }

    setPending(null);

    if (confirmable) {
      setShareToken('');
      await waitForShare(request, token);
      return;
    }

    // 전송 확인이 안 되는 플랫폼 — 띄운 시점에 바로 청구한다.
    setBusy(true);
    try {
      const response = await submitEventShare(
        slug,
        userSeq,
        request.missionSeq,
        request.platform,
        enteredMobile
      );
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }
      setProgress(response.data ?? EMPTY_PROGRESS);
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  /**
   * 카카오가 이 공유를 어떻게 처리했는지 서버에 묻는다.
   *
   * ★ **응모 수로 짐작하지 않는다.** (2026-09-15)
   *   예전에는 카카오톡을 연 뒤 약 8초 안에 응모 수가 늘지 않으면 "반영되지 않았어요" 를
   *   띄웠다. 그런데 이 8초에는 채팅방을 고르고 보내는 시간까지 들어간다 — 14~17초 걸린
   *   기록이 있다. 콜백이 오기도 전에 확인이 끝나 **정상 지급된 공유에도** 경고가 떴고,
   *   대기 중에 다시 공유하면 대기가 겹쳐 경고가 여러 번 떴다.
   *
   *   이제 토큰이 실제로 어떻게 닫혔는지(지급·거절)를 받아서 **거절됐을 때만** 한 번 알린다.
   *   새 공유를 시작하면 이전 대기는 스스로 멈춘다(`shareWaitRef`).
   *   끝까지 결과가 없으면 공유창을 그냥 닫은 것이다 — 조용히 끝낸다.
   */
  async function waitForShare(request: Extract<Pending, { kind: 'share' }>, token: string) {
    const run = ++shareWaitRef.current;
    const stale = () => run !== shareWaitRef.current;

    // 토큰이 없으면 결과를 물을 수 없다. 다음 공유 준비만 해 둔다.
    if (!token) {
      void issueShareToken(request.missionSeq, request.platform);
      return;
    }

    const deadline = Date.now() + SHARE_WAIT_MS;
    let delay = 1500;

    while (Date.now() < deadline) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      delay = Math.min(Math.round(delay * 1.5), 5000);
      if (stale()) return;

      let result: EventShareStatus | undefined;
      try {
        const response = await fetchShareStatus(slug, userSeq, token);
        result = response.result ? response.data : undefined;
      } catch {
        continue; // 잠깐의 네트워크 문제는 다음 차례에 다시 묻는다.
      }
      if (stale()) return;
      if (!result || result.status === 'W') continue;

      // 결과가 났다. 현황을 새로 읽고, 다음 공유에 쓸 토큰을 받아 둔다.
      try {
        const progressResponse = await fetchEventProgress(slug, userSeq);
        if (progressResponse.result && progressResponse.data) setProgress(progressResponse.data);
      } catch {
        // 현황은 다음 동작 때 다시 읽힌다.
      }
      void issueShareToken(request.missionSeq, request.platform);

      if (result.status === 'X' && !stale()) {
        await dispatch(askAlert(result.message || '응모권이 지급되지 않았어요.'));
      }
      return;
    }

    if (!stale()) void issueShareToken(request.missionSeq, request.platform);
  }

  async function handleSendCode() {
    if (!requireLogin() || busy) return;
    if (!phoneFilled) {
      await dispatch(askAlert('휴대폰 번호를 정확히 입력해 주세요.'));
      return;
    }
    if (resendIn > 0) {
      await dispatch(askAlert(`${resendIn}초 후에 다시 시도해 주세요.`));
      return;
    }

    setBusy(true);
    try {
      const response = await sendMobileCode(userSeq, mobile);
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }
      setCodeSent(true);
      setCode('');
      setResendIn(response.data?.resendIn ?? MOBILE_RESEND_COOLDOWN_SEC);
      setExpiresIn(response.data?.expiresIn ?? MOBILE_CODE_TTL_SEC);
      codeRef.current?.focus();
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyCode() {
    if (busy || code.length < MOBILE_CODE_LENGTH) return;

    setBusy(true);
    try {
      const response = await verifyMobileCode(userSeq, mobile, code);
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }
      setVerifiedMobile(mobile);
      setResendIn(0);
      setExpiresIn(0);
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner show />;
  if (!detail) return null;

  const noteItems = notes.items ?? [];
  const notePages = Math.max(1, notes.total_pages ?? 1);

  /** 항목 종류별 오른쪽 영역 */
  function missionAction(mission: EventMission) {
    const state = progressOf(mission.seq);
    const done = state?.done ?? false;

    if (mission.kind === 'share') {
      return (
        <div class="event-mission-action">
          {mission.platforms.map((platform) => (
            <button
              key={platform.code}
              type="button"
              class={`event-mission-btn event-mission-btn--${platform.id}`}
              disabled={busy || done}
              onClick={() =>
                void submit({
                  kind: 'share',
                  missionSeq: mission.seq,
                  platform: platform.code,
                  platformId: platform.id,
                })
              }
            >
              {platform.id === 'kakao' && (
                <span class="event-mission-btn-icon">
                  <KakaoBubbleIcon />
                </span>
              )}
              {platform.label} 공유하기
            </button>
          ))}
          <p class="event-mission-count">
            현재 응모 횟수 <strong>{state?.entries ?? 0}</strong>회 / 최대 {mission.limit}회
          </p>
        </div>
      );
    }

    if (mission.kind === 'comment') {
      const openVideo = () => {
        if (mission.link) window.open(mission.link, '_blank', 'noopener');
        else void dispatch(askAlert('연결된 영상이 없습니다.'));
      };

      return (
        <div class="event-mission-action-group">
          <img class="event-mission-device" src="/images/phone.svg" alt="" aria-hidden="true" />
          <div class="event-mission-action">
            <a
              href={mission.link ?? '#event'}
              class="event-mission-thumb"
              target={mission.link ? '_blank' : undefined}
              rel="noreferrer"
              onClick={(e) => {
                if (!mission.link) {
                  e.preventDefault();
                  openVideo();
                }
              }}
            >
              <SafeImage src={mission.image} alt="" />
              <span class="event-mission-thumb-play">
                <PlayTriangleIcon />
              </span>
            </a>
            <button
              type="button"
              class="event-mission-btn event-mission-btn--outline"
              onClick={openVideo}
            >
              유튜브 영상 보러가기
            </button>
          </div>

          <form
            class="event-mission-upload"
            onSubmit={(e) => {
              e.preventDefault();
              if (!file) {
                void dispatch(askAlert('인증샷을 첨부해 주세요.'));
                return;
              }
              void submit({ kind: 'upload', missionSeq: mission.seq, file });
            }}
          >
            <div class="event-mission-upload-row">
              <label class="event-mission-upload-btn" for="missionUpload">
                <span class="event-mission-upload-icon">+</span>
                첨부파일
              </label>
              <input
                type="file"
                id="missionUpload"
                accept="image/*"
                hidden
                disabled={done}
                onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
              />
              <span class="event-mission-upload-filename">
                {done ? '이미 제출했습니다' : (file?.name ?? '선택된 파일 없음')}
              </span>
            </div>
            <button type="submit" class="event-mission-upload-submit" disabled={busy || done}>
              {done ? '제출완료' : '제출하기'}
            </button>
          </form>
        </div>
      );
    }

    // 감상평
    return (
      <div class="notes-card">
        <div class="event-mission-comment">
          <textarea
            class="event-mission-comment-input"
            placeholder="이 곡에 대한 감상평을 남겨주세요."
            maxLength={200}
            value={note}
            disabled={done}
            onInput={(e) => setNote(e.currentTarget.value)}
          />
          <button
            type="button"
            class="event-mission-comment-submit"
            disabled={busy || done}
            onClick={() => {
              if (!note.trim()) {
                void dispatch(askAlert('감상평을 입력해 주세요.'));
                return;
              }
              void submit({ kind: 'review', missionSeq: mission.seq, content: note.trim() });
            }}
          >
            {done ? '등록완료' : '등록하기'}
          </button>
        </div>

        <ul class="notes-list">
          {noteItems.map((item, i) => (
            <li key={i} class="notes-row">
              <div class="notes-meta">
                <span class="notes-name">{item.name}</span>
                <span class="notes-date">{item.date}</span>
              </div>
              <span class="notes-comment">{item.comment}</span>
            </li>
          ))}
        </ul>

        {notePages > 1 && (
          <div class="notes-pagination">
            <button
              type="button"
              class="notes-page-btn notes-page-btn--prev"
              aria-label="이전 페이지"
              disabled={notesPage === 1}
              onClick={() => setNotesPage(notesPage - 1)}
            >
              <ChevronIcon direction="prev" />
            </button>
            <span class="notes-page-indicator">
              {notesPage} / {notePages}
            </span>
            <button
              type="button"
              class="notes-page-btn notes-page-btn--next"
              aria-label="다음 페이지"
              disabled={notesPage === notePages}
              onClick={() => setNotesPage(notesPage + 1)}
            >
              <ChevronIcon direction="next" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div class="page">
      <header class="title">
        <NavBack onBack={() => route('/event')} />
        <span class="nav-title">이벤트</span>
      </header>

      <div class="section">
        <div class="contents">
          <main class="webzine">
            <section class="wz-event" id="event">
              <div class="section-inner">
                <div class="event-banner">
                  <div class="event-content">
                    <span class="event-badge">{detail.badge}</span>
                    <h2 class="event-heading">
                      <Heading text={detail.heading} />
                    </h2>
                    <p class="event-desc">
                      <Lines text={detail.description} />
                    </p>
                    <div class="event-prizes" data-columns={prizeColumns(detail.prizes.length)}>
                      {detail.prizes.map((prize) => (
                        <div key={prize.label} class="event-prize">
                          <div class="event-prize-icon">
                            <SafeImage src={prize.icon} alt={prize.label} />
                          </div>
                          <span class="event-prize-badge">
                            {prize.label} <strong>{prize.winners}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div class="event-detail" id="event-missions">
                  {/* 응모권 총수는 보이지 않는다 (2026-09-15 요청). 미션별 CLEAR 표시는 그대로다. */}

                  <div class="event-missions">
                    {detail.missions.map((mission) => {
                      const Icon = MISSION_ICON[mission.kind];
                      const done = progressOf(mission.seq)?.done ?? false;

                      return (
                        <div
                          key={mission.no}
                          class={done ? 'event-mission-row cleared' : 'event-mission-row'}
                          data-mission={mission.no}
                        >
                          {/* 번호는 서버가 노출 순서로 매긴 값이다 */}
                          <span class="event-mission-badge">{mission.badge}</span>
                          <div class="event-mission-box">
                            <div class="event-mission-info">
                              <span class="mission-stamp">CLEAR</span>
                              <div class="event-mission-title">
                                <span
                                  class={`event-mission-icon event-mission-icon--${mission.kind}`}
                                >
                                  {Icon && <Icon />}
                                </span>
                                <strong>{mission.title}</strong>
                              </div>
                              <p>
                                <Lines text={mission.description} />
                              </p>
                            </div>
                            {missionAction(mission)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            <section class="wz-event-notice">
              <div class="section-inner">
                <h3 class="event-notice-title">유의사항</h3>
                <ul class="event-notice-list">
                  {detail.notices.map((text, i) => (
                    <li key={i}>{text}</li>
                  ))}
                  <li class="event-notice-item--link">
                    <button
                      type="button"
                      class="event-notice-link"
                      onClick={() => setPrivacyOpen(true)}
                    >
                      {detail.privacy.title}
                    </button>
                  </li>
                </ul>
              </div>
            </section>
          </main>
        </div>
      </div>

      <TabBar path="/event" />

      {/* 응모 정보 입력 — 발송 번호가 없을 때만 뜬다 */}
      <div class={pending ? 'popup-overlay show' : 'popup-overlay'}>
        <div class="popup-sheet popup-sheet--lg">
          <div class="popup-body popup-body--warning popup-body--mission-submit">
            <h3 class="popup-title">이벤트 응모 정보 입력</h3>
            <p class="popup-desc">
              경품 발송을 위해 휴대폰 번호를 입력하고 아래 내용에 동의해주세요.
            </p>

            <div class="phone-verify-row">
              <div class="phone-inputs">
                <input
                  class="form-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={3}
                  placeholder="010"
                  value={phone1}
                  onInput={numericInput(setPhone1, 3)}
                />
                <span class="input-dash">-</span>
                <input
                  class="form-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="1234"
                  value={phone2}
                  onInput={numericInput(setPhone2, 4)}
                />
                <span class="input-dash">-</span>
                <input
                  class="form-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="5678"
                  value={phone3}
                  onInput={numericInput(setPhone3, 4)}
                />
              </div>
              <button
                type="button"
                class={codeSent ? 'phone-verify-btn is-done' : 'phone-verify-btn'}
                disabled={!phoneFilled || busy || verified}
                onClick={handleSendCode}
              >
                {verified ? '인증완료' : codeSent ? '재발송' : '인증하기'}
              </button>
            </div>

            {codeSent && (
              <div class="phone-code-row">
                <input
                  ref={codeRef}
                  class="form-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={MOBILE_CODE_LENGTH}
                  placeholder="인증번호 입력"
                  value={code}
                  onInput={numericInput(setCode, MOBILE_CODE_LENGTH)}
                  disabled={verified}
                />
                <button
                  type="button"
                  class={verified ? 'phone-verify-btn is-done' : 'phone-verify-btn'}
                  disabled={verified || busy || code.length < MOBILE_CODE_LENGTH}
                  onClick={handleVerifyCode}
                >
                  {verified ? '인증완료' : '확인'}
                </button>
              </div>
            )}

            {codeSent && !verified && expiresIn > 0 && (
              <p class="phone-code-hint">남은 시간 {formatMinSec(expiresIn)}</p>
            )}

            <ul class="mission-consent-list">
              {detail.consents.map((label, i) => (
                <li key={i}>
                  <button
                    type="button"
                    class={consents[i] ? 'mission-consent-check active' : 'mission-consent-check'}
                    onClick={() => setConsents((prev) => prev.map((v, j) => (i === j ? !v : v)))}
                  >
                    <span class="check-circle">
                      <CheckIcon />
                    </span>
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            class="popup-btn"
            disabled={!verified || !allAgreed || busy}
            onClick={() => void confirmAgree()}
          >
            확인
          </button>
        </div>
      </div>

      {/* 개인정보 수집·이용 상세 */}
      <div class={privacyOpen ? 'popup-overlay show' : 'popup-overlay'}>
        <div class="popup-sheet">
          <div class="popup-body popup-body--warning popup-body--mission-submit">
            <h3 class="popup-title">{detail.privacy.title}</h3>
            <div class="mission-submit-info">
              <div class="mission-submit-table-wrap">
                <table class="mission-submit-table">
                  <colgroup>
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '70%' }} />
                  </colgroup>
                  <tbody>
                    {detail.privacy.rows.map((row) => (
                      <tr key={row.label}>
                        <td class="type">{row.label}</td>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p class="mission-submit-note">{detail.privacy.note}</p>
            </div>
          </div>
          <button type="button" class="popup-btn" onClick={() => setPrivacyOpen(false)}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventView;
